import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { DOCS_SECTIONS } from "@/lib/content";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { FileText } from "lucide-react";

export default function DocsPage() {
  return (
    <Layout>
      <SEOHead
        title="Documentation | ClauseArk"
        description="An overview of how ClauseArk fits together — from contract intake through review, redlines, and the lifecycle."
      />

      <section className="pt-24 pb-20 md:pt-32 md:pb-24 border-b border-border/50 bg-slate-50 dark:bg-navy/30">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <h1 className="text-display mb-6 text-navy dark:text-white">
              Documentation
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300">
              An overview of how ClauseArk fits together — from contract intake through review, redlines, and the lifecycle. Detailed in-product help is available to customers inside the app.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {DOCS_SECTIONS.map((section, i) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-card rounded-2xl border p-8 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                    <FileText className="h-5 w-5" />
                  </div>
                  <h2 className="text-xl font-bold">{section.title}</h2>
                </div>

                <ul className="space-y-5">
                  {section.items.map((item, j) => (
                    <li key={j}>
                      {item.path ? (
                        <Link
                          href={item.path}
                          className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline mb-1 inline-block cursor-pointer"
                          data-testid={`docs-item-link-${item.path.replace(/[^a-z0-9]+/gi, "-")}`}
                        >
                          {item.label}
                        </Link>
                      ) : (
                        <div className="text-sm font-semibold text-foreground mb-1">
                          {item.label}
                        </div>
                      )}
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.body}
                      </p>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <div className="mt-20 bg-navy text-white rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-brand-gradient opacity-10 mix-blend-overlay" />
            <div className="relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Need a hand?</h2>
              <p className="text-slate-300 mb-8 max-w-xl mx-auto">
                Existing customer? Reach support inside the app or email support@clauseark.com. Otherwise, we'd be happy to walk through ClauseArk live.
              </p>
              <Link href="/company/contact">
                <span className="inline-flex h-12 items-center justify-center rounded-md bg-white px-8 text-sm font-semibold text-navy hover:bg-slate-100 transition-colors cursor-pointer">
                  Contact us
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
